import React, { useState, useMemo, useCallback, useRef } from 'react';
import * as XLSX from 'xlsx';
import { sans, mono, green, greenLight, red, redLight, redDark, amber, amberLight, useTheme } from '../tokens';
import PANEL_CATALOG, { STATEMENT_TYPES, CATEGORIES } from '../data/panelCatalog';

const teal = '#0D9488';
const purple = '#7C3AED';
const purpleLight = '#EDE9FE';

// ══════════════════════════════════════════════════════════════════════
// TB Import → Auto-fill Section/Panel Mapping
// ══════════════════════════════════════════════════════════════════════
//
// When a Trial Balance file (CSV, XLS, XLSX) is uploaded, each GL account
// is automatically mapped to one or more close panels (sections) using a
// 3-step cascade:
//
//   1. GL Range Match (highest priority)
//      Each panel in PANEL_CATALOG can define a glRange (e.g. "10000-10999").
//      If the account number falls within a panel's range, it maps there.
//      This is the most reliable method — works even if account names are
//      in French or abbreviated.
//
//   2. Name-based Pattern Match (fallback)
//      If no GL range matches, the account name is tested against NAME_RULES
//      — a list of regex patterns that cover common account names for cash,
//      AR, AP, payroll, revenue, COGS, etc. The first matching rule wins.
//      Patterns are bilingual (EN/FR) where possible.
//
//   3. Secondary Panel Tags (additive)
//      After the primary panel is assigned, SECONDARY_RULES scan the name
//      for keywords that indicate the account should ALSO appear in an
//      adjustment panel (e.g. USD accounts → FX Adjustments, lease accounts
//      → Lease Adjustments). These are additive — they don't replace the
//      primary mapping.
//
// Accounts that don't match any rule are left unmapped and shown with a
// yellow "Unmapped" badge so the user can manually assign them.
//
// After auto-mapping, the user can override any mapping via dropdowns
// before confirming the import.
// ══════════════════════════════════════════════════════════════════════

// ─── GL Range parsing ───
function parseRange(glRange) {
  if (!glRange) return null;
  const match = glRange.match(/^(\d+)\s*[-\u2013]\s*(\d+)$/);
  if (match) return { min: parseInt(match[1], 10), max: parseInt(match[2], 10) };
  const single = glRange.match(/^(\d+)$/);
  if (single) { const n = parseInt(single[1], 10); return { min: n, max: n }; }
  return null;
}

// ─── AI-powered smart mapping ───
// First tries GL range, then uses account name patterns to guess the best panel.
// Returns array of panel IDs (primary + secondary panels).

const NAME_RULES = [
  // NOTE: Patterns use \b only at the START (word boundary).
  // No trailing \b — it breaks partial-word matches like "receiv" in "receivable"
  // or "prepaid" in "prepaids" or "deprec" in "depreciation".
  //
  // Cash & banking
  { pattern: /\b(bank|chequ|check|chequing|checking|savings|cash|petty\s*cash|operating|payroll.*acct)/i, primary: 'bs-ca-cash' },
  // Float / corporate cards
  { pattern: /\b(float|corporate\s*card|amex|visa|mastercard)/i, primary: 'bs-ca-cash' },
  // Clearing accounts → always Cash & Cash Equivalents (they net to zero, not interco)
  { pattern: /\b(clearing)/i, primary: 'bs-ca-cash' },
  // GIC / investments
  { pattern: /\b(gic|guaranteed\s*invest|t-bill|treasury\s*bill|term\s*deposit|money\s*market)/i, primary: 'bs-ca-st-investments' },
  // Interest receivable → Short-term Investments (not AR)
  { pattern: /\b(interest\s*receiv)/i, primary: 'bs-ca-st-investments' },
  // AR
  { pattern: /\b(accounts?\s*receiv|trade\s*receiv|customer)/i, primary: 'bs-ca-ar' },
  // Allowance
  { pattern: /\b(allowance|doubtful|bad\s*debt\s*prov|provision.*receiv)/i, primary: 'bs-ca-allowance' },
  // Accrued lease inducement → Other Receivables
  { pattern: /\b(lease\s*induce|inducement)/i, primary: 'bs-ca-other-receivables' },
  // Grants / SR&ED receivable (BS only — grant revenue is handled below in P&L section)
  { pattern: /\b(sr&ed|sred|government\s*receiv|grant\s*receiv|lhi|leasehold\s*improve)/i, primary: 'bs-ca-other-receivables' },
  // Inventory
  { pattern: /\b(inventor|raw\s*material|wip|work\s*in\s*progress|finished\s*good)/i, primary: 'bs-ca-inventory' },
  // Prepaids — includes deposits and deferred expenses
  { pattern: /\b(prepaid|prepay|advance\s*payment|deposit|security\s*deposit|rental\s*deposit|vendor\s*deposit|deferred\s*expense)/i, primary: 'bs-ca-prepaids' },
  // Sales tax
  { pattern: /\b(gst|hst|qst|sales\s*tax|input\s*tax|vat|tax\s*receiv)/i, primary: 'bs-ca-taxes-receivable' },
  // Fixed assets
  { pattern: /\b(equipment|furniture|fixture|vehicle|building|land|leasehold|computer\s*equip|machin|mold|tool)/i, primary: 'bs-nca-ppe' },
  // Accumulated depreciation → PP&E panel (fixed asset rollforward: cost + accum dep + expense together)
  { pattern: /\b(accum.*deprec|amort.*accum|contra.*asset)/i, primary: 'bs-nca-ppe' },
  // Intangibles
  { pattern: /\b(patent|trademark|software|license|intangible|ip\s|intellect)/i, primary: 'bs-nca-intangibles' },
  // ROU / Leases
  { pattern: /\b(right.*use|rou|lease\s*asset)/i, primary: 'bs-nca-rou' },
  // AP
  { pattern: /\b(accounts?\s*payab|trade\s*payab|a[./]?p\b|supplier)/i, primary: 'bs-cl-ap' },
  // Credit card payable
  { pattern: /\b(credit\s*card|cc\s*payab|cc\s*clearing)/i, primary: 'bs-cl-other' },
  // Accrued expenses
  { pattern: /\b(accru.*expense|accru.*liabil|accrual)/i, primary: 'bs-cl-accrued-expenses' },
  // Accrued payroll
  { pattern: /\b(accru.*payroll|accru.*salary|salary.*payab|wage.*payab|bonus.*payab)/i, primary: 'bs-cl-accrued-payroll' },
  // Payroll liabilities
  { pattern: /\b(source\s*deduct|rrsp|pension\s*payab|benefit.*payab|ei\s*payab|cpp|qpp|health\s*tax)/i, primary: 'bs-cl-payroll-liabilities' },
  // Taxes payable
  { pattern: /\b(tax.*payab|income\s*tax.*payab|gst.*payab|hst.*payab|qst.*payab)/i, primary: 'bs-cl-taxes-payable' },
  // Deferred revenue
  { pattern: /\b(deferred\s*rev|unearned|advance.*customer|prepaid.*rev)/i, primary: 'bs-cl-deferred-revenue' },
  // Deferred rent liabilities / Other payables → Other Current Liabilities
  { pattern: /\b(deferred\s*rent|rent.*liabil|other\s*payab)/i, primary: 'bs-cl-other' },
  // Deemed interest → Long-term Debt (imputed interest on non-arm's length loans)
  { pattern: /\b(deemed\s*interest)/i, primary: 'bs-ncl-ltd' },
  // Debt / loans
  { pattern: /\b(loan|debt|line\s*of\s*credit|mortgage|note.*payab|bdc|dec\s*loan|bank\s*loan)/i, primary: 'bs-ncl-ltd' },
  // Lease liabilities
  { pattern: /\b(lease\s*liab|lease\s*oblig|rou.*liab)/i, primary: 'bs-ncl-lease' },
  // Share issuance costs → Share Capital
  { pattern: /\b(share\s*issuance|issuance\s*cost|issue\s*cost|share\s*issue)/i, primary: 'bs-eq-share-capital' },
  // Share capital / equity
  { pattern: /\b(share\s*capital|common\s*share|preferred\s*share|stock|class\s*[a-z])/i, primary: 'bs-eq-share-capital' },
  // APIC / contributed surplus
  { pattern: /\b(contributed\s*surplus|paid.in.*capital|apic|share\s*premium|stock\s*option\s*expense)/i, primary: 'bs-eq-apic' },
  // Retained earnings / accumulated deficit → Other Equity (not a standalone close panel)
  { pattern: /\b(retained\s*earn|accumul.*deficit|opening\s*balance.*equity)/i, primary: 'bs-eq-other' },
  // Grant revenue / government grants (P&L) → Other Revenue
  { pattern: /\b(grant\s*rev|revenue.*grant|government.*grant|subsidi.*income|subsidi.*rev)/i, primary: 'pl-rev-other' },
  // Interest earned / interest income → Other Revenue
  { pattern: /\b(interest\s*income|interest\s*earn)/i, primary: 'pl-rev-other' },
  // Revenue
  { pattern: /\b(revenue|sales|product\s*rev|service\s*rev|licensing|subscription)/i, primary: 'pl-rev-product' },
  // COGS
  { pattern: /\b(cost.*goods|cogs|cost.*sales|direct\s*material|direct\s*labor|manufacturing)/i, primary: 'pl-cogs-cogs' },
  // Salaries & payroll
  { pattern: /\b(salary|salaries|wages|payroll|compensation)/i, primary: 'pl-opex-salaries' },
  // Benefits
  { pattern: /\b(benefit|health\s*insur|dental|group\s*insur|rrsp\s*match)/i, primary: 'pl-opex-benefits' },
  // R&D
  { pattern: /\b(research|r&d|development\s*cost|clinical|prototype)/i, primary: 'pl-opex-rd' },
  // Rent & Facilities (including LHI rent expense)
  { pattern: /\b(rent|lease\s*expense|facilities|lhi)/i, primary: 'pl-opex-rent' },
  // Professional fees
  { pattern: /\b(professional\s*fee|consult|advisor|audit\s*fee|accounting\s*fee|legal\s*fee)/i, primary: 'pl-opex-professional' },
  // Insurance
  { pattern: /\b(insurance|d&o|liability\s*insur)/i, primary: 'pl-opex-insurance' },
  // Software / IT
  { pattern: /\b(software|saas|cloud|hosting|it\s*expense|telecom|internet)/i, primary: 'pl-opex-software' },
  // Travel
  { pattern: /\b(travel|flight|hotel|meal|entertainment|conference)/i, primary: 'pl-opex-travel' },
  // Office
  { pattern: /\b(office|supplies|postage|shipping|courier)/i, primary: 'pl-opex-office' },
  // Depreciation & amortization expense → PP&E panel (rollforward: cost + accum dep + expense together)
  { pattern: /\b(depreciation|amortization)/i, primary: 'bs-nca-ppe' },
  // Interest expense / bank charges → Long-term Debt (reconciled as part of the debt schedule)
  { pattern: /\b(interest\s*expense|bank\s*charge|bank\s*fee)/i, primary: 'bs-ncl-ltd' },
  // FX
  { pattern: /\b(foreign\s*exchange|fx\s|f\.?x\.?\s|exchange\s*(gain|loss)|currency\s*(gain|loss)|unrealized)/i, primary: 'pl-other-fx' },
  // Income tax
  { pattern: /\b(income\s*tax\s*expense|corporate\s*tax|tax\s*provision)/i, primary: 'pl-other-income-tax' },
  // Intercompany (excluding clearing — clearing accounts go to cash above)
  { pattern: /\b(intercompany|inter-company|due\s*to|due\s*from)/i, primary: 'bs-ca-interco-receivables' },
];

// Secondary panel rules: accounts that should ALSO appear in another panel
const SECONDARY_RULES = [
  // USD accounts also appear in FX Adjustments
  { pattern: /\busd\b/i, secondary: 'adj-fx' },
  // Lease-related accounts also in Lease Adjustments
  { pattern: /\b(lease|rou|right.*use)\b/i, secondary: 'adj-lease' },
  // Payroll accounts also in Payroll Adjustments
  { pattern: /\b(salary|payroll|wage|benefit|rrsp)\b/i, secondary: 'adj-payroll' },
  // Tax accounts also in Tax Adjustments
  { pattern: /\b(gst|hst|qst|tax)\b/i, secondary: 'adj-tax' },
];

function smartMapAccount(accountCode, accountName, panels) {
  const num = parseInt(String(accountCode).replace(/\D/g, ''), 10);
  const panelIds = [];

  // Step 1: Try GL range match (most reliable)
  if (!isNaN(num)) {
    for (const panel of panels) {
      const range = parseRange(panel.glRange);
      if (range && num >= range.min && num <= range.max) {
        panelIds.push(panel.id);
        break;
      }
    }
  }

  // Step 2: If no range match, try name-based AI rules
  if (panelIds.length === 0 && accountName) {
    for (const rule of NAME_RULES) {
      if (rule.pattern.test(accountName)) {
        // Only add if this panel exists in the active set
        if (panels.find(p => p.id === rule.primary)) {
          panelIds.push(rule.primary);
          break;
        }
      }
    }
  }

  // Step 3: Add secondary panel tags based on name
  if (accountName) {
    for (const rule of SECONDARY_RULES) {
      if (rule.pattern.test(accountName)) {
        const secPanel = panels.find(p => p.id === rule.secondary);
        if (secPanel && !panelIds.includes(rule.secondary)) {
          panelIds.push(rule.secondary);
        }
      }
    }
  }

  return panelIds;
}

// ─── Detect columns from merged header text ───
// Patterns match individual words or combined multi-row headers
const HEADER_PATTERNS = {
  code: /^(account\s*(no|number|code|#)|no\s*de?\s*compte|compte|acct|code|number)/i,
  name: /^(account\s*(name|title|desc)|description|titre|nom|libelle|name)/i,
  debit: /^(debit|d[e\u00e9]bit)/i,
  credit: /^(credit|cr[e\u00e9]dit)/i,
  balance: /^(closing\s*balance|balance|net|solde|ending|end\s*bal|on\s+\d)/i,
  opening: /^(opening\s*balance|opening|ouverture|on\s+\d)/i,
};

function detectColumns(mergedRow) {
  const cols = { code: -1, name: -1, debit: -1, credit: -1, balance: -1 };
  mergedRow.forEach((cell, i) => {
    const val = String(cell || '').trim();
    if (!val) return;
    for (const [key, pattern] of Object.entries(HEADER_PATTERNS)) {
      if (key === 'opening') continue; // skip, we just need to not confuse it with closing
      if (pattern.test(val) && cols[key] === -1) {
        cols[key] = i;
      }
    }
  });
  return cols;
}

// Merge two adjacent rows into combined header strings
function mergeHeaderRows(row1, row2) {
  const len = Math.max(row1?.length || 0, row2?.length || 0);
  const merged = [];
  for (let i = 0; i < len; i++) {
    const a = String(row1?.[i] || '').trim();
    const b = String(row2?.[i] || '').trim();
    merged.push(a && b ? `${a} ${b}` : a || b);
  }
  return merged;
}

function isHeaderLike(row) {
  // A row is header-like if it has "account" or "number" or "debit" in any cell
  return row.some(cell => /account|number|name|debit|credit|balance|solde|compte/i.test(String(cell || '')));
}

// ─── Parse file into rows of { code, name, debit, credit } ───
function parseSheetData(rows) {
  // Strategy: scan first 20 rows for header patterns.
  // Handle Sage Intacct format: multi-row headers with metadata rows above.
  let headerIdx = -1;
  let cols = null;
  let dataStartIdx = -1;

  for (let i = 0; i < Math.min(rows.length, 20); i++) {
    const row = rows[i];
    if (!row || row.length === 0) continue;

    // Try single-row header detection
    const singleCols = detectColumns(row.map(c => String(c || '').trim()));
    if (singleCols.code !== -1 && (singleCols.debit !== -1 || singleCols.credit !== -1 || singleCols.balance !== -1)) {
      headerIdx = i;
      cols = singleCols;
      dataStartIdx = i + 1;
      break;
    }

    // Try merging with the next row (multi-row header like Sage TB)
    if (i + 1 < rows.length && isHeaderLike(row)) {
      const merged = mergeHeaderRows(row, rows[i + 1]);
      const mergedCols = detectColumns(merged);
      if (mergedCols.code !== -1 && (mergedCols.debit !== -1 || mergedCols.credit !== -1 || mergedCols.balance !== -1)) {
        headerIdx = i;
        cols = mergedCols;
        dataStartIdx = i + 2; // skip both header rows
        break;
      }
    }
  }

  if (headerIdx === -1 || !cols || cols.code === -1) {
    // Fallback: try to detect by looking for a row where col 0 is a 5-digit number
    for (let i = 0; i < Math.min(rows.length, 20); i++) {
      const row = rows[i];
      if (!row) continue;
      const firstCell = String(row[0] || '').trim();
      if (/^\d{4,5}$/.test(firstCell) && row.length >= 4) {
        // Assume: col 0 = code, col 1 = name, col 3 = debit, col 4 = credit
        cols = { code: 0, name: 1, debit: 3, credit: 4, balance: -1 };
        if (row.length >= 6) cols.balance = 5;
        dataStartIdx = i;
        break;
      }
    }
  }

  if (!cols || cols.code === -1 || dataStartIdx === -1) return [];

  // For Sage TB: if we have both "opening" and "closing" balance columns,
  // prefer the closing balance (last numeric column)
  // The Sage format is: Account Number | Account Name | Opening Balance | Debit | Credit | Closing Balance
  // So balance = col 5 (closing), debit = col 3, credit = col 4

  const accounts = [];
  for (let i = dataStartIdx; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.length === 0) continue;
    const code = String(row[cols.code] || '').trim();
    if (!code || !/^\d/.test(code)) continue; // Must start with a digit

    const name = cols.name !== -1 ? String(row[cols.name] || '').trim() : '';
    let debit = 0, credit = 0, closingBalance = null;

    if (cols.debit !== -1) debit = parseFloat(String(row[cols.debit] || '0').replace(/[^0-9.-]/g, '')) || 0;
    if (cols.credit !== -1) credit = parseFloat(String(row[cols.credit] || '0').replace(/[^0-9.-]/g, '')) || 0;
    if (cols.balance !== -1) {
      closingBalance = parseFloat(String(row[cols.balance] || '0').replace(/[^0-9.-]/g, '')) || 0;
    }

    // If we only have closing balance (no debit/credit), use it
    if (closingBalance !== null && cols.debit === -1 && cols.credit === -1) {
      if (closingBalance >= 0) debit = closingBalance; else credit = Math.abs(closingBalance);
    }

    // Compute balance: prefer closing balance if available, otherwise debit - credit
    const balance = closingBalance !== null ? closingBalance : (debit - credit);

    // Skip accounts with zero everything
    if (debit === 0 && credit === 0 && (closingBalance === null || closingBalance === 0)) continue;

    accounts.push({ code, name, debit, credit, balance });
  }
  return accounts;
}

// ─── Format currency ───
function fmt(n) {
  if (n === 0) return '--';
  const abs = Math.abs(n);
  const str = abs.toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return n < 0 ? `$(${str})` : `$${str}`;
}

// ─── Component ───
export default function TBUploadPanel({ activePanelIds, selectedMonth, onConfirm, onCreatePanel, onClose }) {
  const t = useTheme();
  const fileInputRef = useRef(null);
  const [step, setStep] = useState('upload'); // upload | preview | mapping | done
  const [fileName, setFileName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const [parsedAccounts, setParsedAccounts] = useState([]);
  // mappings: { accountCode: [panelId, ...] } — array for multi-panel support
  const [mappings, setMappings] = useState({});
  const [autoMapped, setAutoMapped] = useState(new Set());
  // Inline panel creation
  const [showCreatePanel, setShowCreatePanel] = useState(null); // account code that triggered it
  const [newPanelName, setNewPanelName] = useState('');
  const [newPanelType, setNewPanelType] = useState('generic');
  const [newPanelStatement, setNewPanelStatement] = useState('balance_sheet');
  const [newPanelCategory, setNewPanelCategory] = useState('current_assets');

  // All panels (for dropdowns)
  const allPanels = useMemo(() => {
    return [...PANEL_CATALOG].sort((a, b) => {
      const aNum = parseRange(a.glRange)?.min || 99999;
      const bNum = parseRange(b.glRange)?.min || 99999;
      return aNum - bNum;
    });
  }, []);

  // ─── File handling ───
  const handleFile = useCallback(async (file) => {
    setError(null);
    setFileName(file.name);
    const ext = file.name.toLowerCase().split('.').pop();

    try {
      if (ext === 'csv') {
        const text = await file.text();
        const rows = text.split(/\r?\n/).map(line => {
          // Handle both comma and semicolon delimiters
          const sep = line.includes(';') ? ';' : ',';
          return line.split(sep).map(cell => cell.replace(/^"(.*)"$/, '$1').trim());
        });
        const accounts = parseSheetData(rows);
        if (accounts.length === 0) {
          setError('Could not detect account data. Make sure the file has columns for Account Number, Debit/Credit or Balance.');
          return;
        }
        setParsedAccounts(accounts);
        runAutoMapping(accounts);
        setStep('preview');
      } else if (['xls', 'xlsx', 'xlsm'].includes(ext)) {
        const buffer = await file.arrayBuffer();
        const workbook = XLSX.read(buffer, { type: 'array' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
        const accounts = parseSheetData(rows);
        if (accounts.length === 0) {
          setError('Could not detect account data. Make sure the file has columns for Account Number, Debit/Credit or Balance.');
          return;
        }
        setParsedAccounts(accounts);
        runAutoMapping(accounts);
        setStep('preview');
      } else if (ext === 'pdf') {
        setError('PDF parsing requires backend connection. Please export your TB as CSV or Excel for now.');
      } else {
        setError(`Unsupported file type: .${ext}. Please use CSV, XLS, or XLSX.`);
      }
    } catch (err) {
      console.error('TB parse error:', err);
      setError(`Error parsing file: ${err.message}`);
    }
  }, [allPanels]); // eslint-disable-line react-hooks/exhaustive-deps

  const runAutoMapping = (accounts) => {
    const map = {};
    const autoSet = new Set();
    accounts.forEach(acc => {
      const panelIds = smartMapAccount(acc.code, acc.name, allPanels);
      if (panelIds.length > 0) {
        map[acc.code] = panelIds;
        autoSet.add(acc.code);
      }
    });
    setMappings(map);
    setAutoMapped(autoSet);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const files = e.dataTransfer?.files;
    if (files && files[0]) handleFile(files[0]);
  }, [handleFile]);

  // ─── Stats ───
  const stats = useMemo(() => {
    const total = parsedAccounts.length;
    const mapped = parsedAccounts.filter(a => mappings[a.code] && mappings[a.code].length > 0).length;
    const unmapped = total - mapped;
    const auto = autoMapped.size;
    const manual = mapped - auto;
    const multiMapped = parsedAccounts.filter(a => mappings[a.code] && mappings[a.code].length > 1).length;

    // Group by panel
    const byPanel = {};
    parsedAccounts.forEach(acc => {
      const pids = mappings[acc.code] || [];
      pids.forEach(pid => {
        if (!byPanel[pid]) byPanel[pid] = { accounts: [], totalDebit: 0, totalCredit: 0 };
        byPanel[pid].accounts.push(acc);
        byPanel[pid].totalDebit += acc.debit;
        byPanel[pid].totalCredit += acc.credit;
      });
    });

    return { total, mapped, unmapped, auto, manual, multiMapped, byPanel };
  }, [parsedAccounts, mappings, autoMapped]);

  // ─── Confirm ───
  // Inline panel creation handler
  const handleCreateInlinePanel = () => {
    if (!newPanelName.trim() || !onCreatePanel) return;
    onCreatePanel({
      label: newPanelName.trim(),
      statementType: newPanelStatement,
      category: newPanelCategory,
      panelType: newPanelType,
      glRange: '',
    });
    // Map the account that triggered the create to the new panel
    const slug = newPanelName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '');
    const newPanelId = `custom-${slug}`;
    if (showCreatePanel) {
      const current = mappings[showCreatePanel] || [];
      setMappings({ ...mappings, [showCreatePanel]: [...current, newPanelId] });
    }
    setShowCreatePanel(null);
    setNewPanelName('');
    setNewPanelType('generic');
  };

  const handleConfirm = () => {
    // Flatten multi-panel mappings: each account appears once per panel it's mapped to
    // The primary panel (index 0) is the "home" panel; others are secondary references
    const flatAccounts = [];
    parsedAccounts.forEach(acc => {
      const panelIds = mappings[acc.code] || [];
      if (panelIds.length === 0) {
        flatAccounts.push({ ...acc, panelId: null, isSecondary: false });
      } else {
        panelIds.forEach((pid, idx) => {
          flatAccounts.push({
            code: acc.code,
            name: acc.name,
            debit: acc.debit,
            credit: acc.credit,
            balance: acc.balance,
            panelId: pid,
            isSecondary: idx > 0,
          });
        });
      }
    });
    const result = {
      accounts: flatAccounts,
      month: selectedMonth,
      uploadedAt: new Date().toISOString(),
      fileName,
    };
    localStorage.setItem(`finkloz_gl_mappings_${selectedMonth}`, JSON.stringify(result));
    // Also save a month-agnostic copy of the chart of accounts (same GL list every month)
    const uniqueAccounts = [];
    const seen = new Set();
    for (const a of flatAccounts) {
      if (!seen.has(a.code)) { seen.add(a.code); uniqueAccounts.push({ code: a.code, name: a.name }); }
    }
    localStorage.setItem('finkloz_chart_of_accounts', JSON.stringify(uniqueAccounts));
    if (onConfirm) onConfirm(result);
    setStep('done');
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      background: t.overlay || 'rgba(0,0,0,0.4)', zIndex: 200,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        width: step === 'upload' ? 520 : 800, maxHeight: '90vh',
        background: t.bgCard || '#fff', borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        transition: 'width 0.3s',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 24px', borderBottom: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans }}>
              Import Trial Balance
            </div>
            <div style={{ fontSize: 11, color: t.textFaint, marginTop: 2 }}>
              {step === 'upload' && 'Upload your TB to auto-map GL accounts to panels'}
              {step === 'preview' && `${stats.total} accounts found -- ${stats.mapped} mapped, ${stats.unmapped} unmapped`}
              {step === 'mapping' && 'Review and fix account-to-panel mappings'}
              {step === 'done' && 'GL accounts mapped successfully'}
            </div>
          </div>
          <button onClick={onClose} style={{
            border: 'none', background: t.inputBg || '#F1F5F9', borderRadius: 6, width: 28, height: 28,
            cursor: 'pointer', fontSize: 14, color: t.textSecondary,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>&times;</button>
        </div>

        {/* ─── Step 1: Upload ─── */}
        {step === 'upload' && (
          <div style={{ padding: '24px', flex: 1 }}>
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              style={{
                border: `2px dashed ${dragOver ? teal : t.border}`,
                borderRadius: 12, padding: '48px 24px', textAlign: 'center',
                cursor: 'pointer', transition: 'all 0.15s',
                background: dragOver ? teal + '08' : 'transparent',
              }}
            >
              <div style={{ fontSize: 32, marginBottom: 12 }}>&#128203;</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: t.textPrimary, fontFamily: sans, marginBottom: 6 }}>
                Drop your Trial Balance here
              </div>
              <div style={{ fontSize: 12, color: t.textMuted }}>
                Supports CSV, XLS, XLSX
              </div>
              <div style={{ fontSize: 10, color: t.textFaint, marginTop: 8 }}>
                The file should have columns for Account Number, Account Name, and Debit/Credit or Balance
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xls,.xlsx,.xlsm"
                style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) handleFile(e.target.files[0]); }}
              />
            </div>
            {error && (
              <div style={{ marginTop: 12, padding: '10px 14px', borderRadius: 8, background: redLight, color: redDark, fontSize: 12 }}>
                {error}
              </div>
            )}
          </div>
        )}

        {/* ─── Step 2: Preview + Mapping ─── */}
        {(step === 'preview' || step === 'mapping') && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Stats bar */}
            <div style={{ padding: '10px 24px', background: t.bgSurface || '#F8FAFC', borderBottom: `1px solid ${t.border}`, display: 'flex', gap: 20 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: t.textPrimary }}>{stats.total}</div>
                <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Accounts</div>
              </div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: green }}>{stats.mapped}</div>
                <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Mapped</div>
              </div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: stats.unmapped > 0 ? red : green }}>{stats.unmapped}</div>
                <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Unmapped</div>
              </div>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: teal }}>{stats.auto}</div>
                <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Auto</div>
              </div>
              {stats.manual > 0 && (
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: amber }}>{stats.manual}</div>
                  <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Manual</div>
                </div>
              )}
              {stats.multiMapped > 0 && (
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, fontFamily: mono, color: purple }}>{stats.multiMapped}</div>
                  <div style={{ fontSize: 9, color: t.textFaint, textTransform: 'uppercase' }}>Multi-panel</div>
                </div>
              )}
              <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 10, color: t.textFaint }}>&#128196; {fileName}</span>
              </div>
            </div>

            {/* Account table */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '0' }}>
              {/* Column headers */}
              <div style={{
                display: 'grid', gridTemplateColumns: '80px 1fr 90px 90px 90px 200px',
                padding: '8px 24px', gap: 8, borderBottom: `2px solid ${t.border}`,
                position: 'sticky', top: 0, background: t.bgCard || '#fff', zIndex: 1,
              }}>
                <span style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase' }}>Account</span>
                <span style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase' }}>Name</span>
                <span style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', textAlign: 'right' }}>Debit</span>
                <span style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', textAlign: 'right' }}>Credit</span>
                <span style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase', textAlign: 'right' }}>Balance</span>
                <span style={{ fontSize: 9, fontWeight: 700, color: t.textFaint, textTransform: 'uppercase' }}>Panel</span>
              </div>

              {parsedAccounts.map((acc, i) => {
                const panelIds = mappings[acc.code] || [];
                const primaryId = panelIds[0] || '';
                const secondaryIds = panelIds.slice(1);
                const isAuto = autoMapped.has(acc.code);
                const isMapped = panelIds.length > 0;
                const hasSecondary = secondaryIds.length > 0;

                return (
                  <div key={`${acc.code}-${i}`} style={{
                    borderBottom: `1px solid ${t.borderLight || t.border}`,
                    background: !isMapped ? redLight + '20' : isAuto ? 'transparent' : amberLight + '20',
                  }}>
                    <div style={{
                      display: 'grid', gridTemplateColumns: '80px 1fr 90px 90px 90px 200px',
                      padding: '6px 24px', gap: 8, alignItems: 'center',
                    }}>
                      {/* Account code */}
                      <span style={{ fontSize: 12, fontFamily: mono, fontWeight: 600, color: t.textPrimary }}>{acc.code}</span>

                      {/* Name */}
                      <span style={{ fontSize: 11, fontFamily: sans, color: t.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {acc.name}
                      </span>

                      {/* Debit */}
                      <span style={{ fontSize: 11, fontFamily: mono, textAlign: 'right', color: acc.debit > 0 ? t.textPrimary : t.textFaint }}>
                        {acc.debit > 0 ? fmt(acc.debit) : '--'}
                      </span>

                      {/* Credit */}
                      <span style={{ fontSize: 11, fontFamily: mono, textAlign: 'right', color: acc.credit > 0 ? t.textPrimary : t.textFaint }}>
                        {acc.credit > 0 ? fmt(acc.credit) : '--'}
                      </span>

                      {/* Balance */}
                      <span style={{ fontSize: 11, fontFamily: mono, textAlign: 'right', fontWeight: 600, color: acc.balance < 0 ? red : t.textPrimary }}>
                        {fmt(acc.balance)}
                      </span>

                      {/* Primary panel selector */}
                      <select
                        value={primaryId}
                        onChange={e => {
                          const newMap = { ...mappings };
                          if (e.target.value === '__CREATE__') {
                            setShowCreatePanel(acc.code);
                            return;
                          }
                          if (e.target.value) {
                            newMap[acc.code] = [e.target.value, ...secondaryIds];
                          } else {
                            delete newMap[acc.code];
                          }
                          setMappings(newMap);
                          if (isAuto) {
                            const newAuto = new Set(autoMapped);
                            newAuto.delete(acc.code);
                            setAutoMapped(newAuto);
                          }
                        }}
                        style={{
                          width: '100%', padding: '3px 6px', borderRadius: 4,
                          border: `1px solid ${!isMapped ? red + '60' : isAuto ? green + '40' : amber + '60'}`,
                          background: t.bgCard || '#fff',
                          fontSize: 10, fontFamily: sans, color: t.textPrimary,
                          outline: 'none',
                        }}
                      >
                        <option value="">-- Unmapped --</option>
                        {allPanels.map(p => (
                          <option key={p.id} value={p.id}>
                            {p.label} {p.glRange ? `(${p.glRange})` : ''}
                          </option>
                        ))}
                        <option value="__CREATE__">+ Create new panel...</option>
                      </select>
                    </div>

                    {/* Secondary panel tags */}
                    {(hasSecondary || isMapped) && (
                      <div style={{ padding: '0 24px 4px 104px', display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                        {secondaryIds.map(sid => {
                          const sp = PANEL_CATALOG.find(p => p.id === sid);
                          return (
                            <span key={sid} style={{
                              fontSize: 8, padding: '1px 6px', borderRadius: 8,
                              background: purpleLight, color: purple, fontWeight: 500,
                              display: 'inline-flex', alignItems: 'center', gap: 3,
                            }}>
                              {sp?.label || sid}
                              <span
                                onClick={() => {
                                  const newMap = { ...mappings };
                                  newMap[acc.code] = panelIds.filter(id => id !== sid);
                                  if (newMap[acc.code].length === 0) delete newMap[acc.code];
                                  setMappings(newMap);
                                }}
                                style={{ cursor: 'pointer', fontSize: 10, lineHeight: 1 }}
                              >&times;</span>
                            </span>
                          );
                        })}
                        {isMapped && (
                          <select
                            value=""
                            onChange={e => {
                              if (!e.target.value || e.target.value === '__CREATE__') {
                                if (e.target.value === '__CREATE__') setShowCreatePanel(acc.code);
                                return;
                              }
                              if (panelIds.includes(e.target.value)) return;
                              setMappings({ ...mappings, [acc.code]: [...panelIds, e.target.value] });
                            }}
                            style={{
                              padding: '0px 4px', borderRadius: 4,
                              border: `1px dashed ${t.border}`,
                              background: 'transparent',
                              fontSize: 8, fontFamily: sans, color: t.textFaint,
                              outline: 'none', cursor: 'pointer',
                            }}
                          >
                            <option value="">+ also in...</option>
                            {allPanels.filter(p => !panelIds.includes(p.id)).map(p => (
                              <option key={p.id} value={p.id}>{p.label}</option>
                            ))}
                            <option value="__CREATE__">+ Create new panel...</option>
                          </select>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ─── Step 3: Done ─── */}
        {step === 'done' && (
          <div style={{ padding: '40px 24px', textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>&#9989;</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans, marginBottom: 8 }}>
              Trial Balance imported
            </div>
            <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 4 }}>
              {stats.mapped} accounts mapped to {Object.keys(stats.byPanel).length} panels
            </div>
            {stats.unmapped > 0 && (
              <div style={{ fontSize: 12, color: amber }}>
                {stats.unmapped} accounts unmapped (you can fix this later)
              </div>
            )}
          </div>
        )}

        {/* ─── Inline panel creation popup ─── */}
        {showCreatePanel && (
          <div style={{
            padding: '14px 24px', borderTop: `1px solid ${t.border}`,
            background: greenLight + '20',
          }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: t.textPrimary, fontFamily: sans, marginBottom: 10 }}>
              Create new panel for account {showCreatePanel}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
              <div>
                <label style={{ fontSize: 9, fontWeight: 600, color: t.textFaint, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>Panel name</label>
                <input
                  autoFocus
                  type="text"
                  placeholder="e.g. SR&ED Credits"
                  value={newPanelName}
                  onChange={e => setNewPanelName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateInlinePanel(); if (e.key === 'Escape') setShowCreatePanel(null); }}
                  style={{ width: '100%', padding: '5px 8px', borderRadius: 4, border: `1px solid ${t.border}`, background: t.bgCard || '#fff', fontSize: 11, fontFamily: sans, color: t.textPrimary, outline: 'none', boxSizing: 'border-box' }}
                />
              </div>
              <div>
                <label style={{ fontSize: 9, fontWeight: 600, color: t.textFaint, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>Statement</label>
                <select value={newPanelStatement} onChange={e => { setNewPanelStatement(e.target.value); const fc = CATEGORIES.find(c => c.statementType === e.target.value); setNewPanelCategory(fc?.id || ''); }} style={{ width: '100%', padding: '5px 8px', borderRadius: 4, border: `1px solid ${t.border}`, background: t.bgCard || '#fff', fontSize: 11, fontFamily: sans, color: t.textPrimary, outline: 'none', boxSizing: 'border-box' }}>
                  {STATEMENT_TYPES.map(st => <option key={st.id} value={st.id}>{st.label}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 9, fontWeight: 600, color: t.textFaint, textTransform: 'uppercase', display: 'block', marginBottom: 2 }}>Category</label>
                <select value={newPanelCategory} onChange={e => setNewPanelCategory(e.target.value)} style={{ width: '100%', padding: '5px 8px', borderRadius: 4, border: `1px solid ${t.border}`, background: t.bgCard || '#fff', fontSize: 11, fontFamily: sans, color: t.textPrimary, outline: 'none', boxSizing: 'border-box' }}>
                  {CATEGORIES.filter(c => c.statementType === newPanelStatement).map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowCreatePanel(null); setNewPanelName(''); }} style={{ border: `1px solid ${t.border}`, background: 'transparent', borderRadius: 4, padding: '4px 12px', fontSize: 10, color: t.textMuted, cursor: 'pointer', fontFamily: sans }}>Cancel</button>
              <button disabled={!newPanelName.trim()} onClick={handleCreateInlinePanel} style={{ border: 'none', background: newPanelName.trim() ? green : '#CBD5E1', borderRadius: 4, padding: '4px 12px', fontSize: 10, color: '#fff', cursor: newPanelName.trim() ? 'pointer' : 'not-allowed', fontFamily: sans, fontWeight: 600 }}>Create & assign</button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '12px 24px', borderTop: `1px solid ${t.border}`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          {step === 'upload' && (
            <span style={{ fontSize: 11, color: t.textFaint }}>
              Accepted formats: CSV, XLS, XLSX
            </span>
          )}
          {(step === 'preview' || step === 'mapping') && (
            <button onClick={() => { setStep('upload'); setParsedAccounts([]); setMappings({}); setAutoMapped(new Set()); setFileName(''); }} style={{
              border: `1px solid ${t.border}`, background: 'transparent', borderRadius: 6,
              padding: '6px 14px', fontSize: 11, color: t.textMuted, cursor: 'pointer', fontFamily: sans,
            }}>Upload different file</button>
          )}
          {step === 'done' && <span />}

          <div style={{ display: 'flex', gap: 8 }}>
            {step !== 'done' && (
              <button onClick={onClose} style={{
                border: `1px solid ${t.border}`, background: 'transparent', borderRadius: 6,
                padding: '6px 14px', fontSize: 11, color: t.textMuted, cursor: 'pointer', fontFamily: sans,
              }}>Cancel</button>
            )}
            {(step === 'preview' || step === 'mapping') && (
              <button onClick={handleConfirm} style={{
                border: 'none', background: green, borderRadius: 6,
                padding: '6px 20px', fontSize: 12, color: '#fff', cursor: 'pointer',
                fontFamily: sans, fontWeight: 600,
              }}>
                Confirm mapping ({stats.mapped}/{stats.total})
              </button>
            )}
            {step === 'done' && (
              <button onClick={onClose} style={{
                border: 'none', background: green, borderRadius: 6,
                padding: '6px 20px', fontSize: 12, color: '#fff', cursor: 'pointer',
                fontFamily: sans, fontWeight: 600,
              }}>Done</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
